/**
 * Registration form component
 */
import { authApi } from '@/api/auth';
import { usersApi } from '@/api/users';
import { useAuth } from '@/hooks/useAuth';
import { RegisterRequest } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

const registerSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  organization_id: z.string().uuid('Invalid organization GUID').optional().or(z.literal('')),
});

interface RegisterFormProps {
  onSwitchToLogin: () => void;
  /** Omit outer Paper and large headings (e.g. inside accordion) */
  compact?: boolean;
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSwitchToLogin,
  compact = false,
}) => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterRequest>({
    resolver: zodResolver(registerSchema),
  });

  const onSubmit = async (data: RegisterRequest) => {
    try {
      setError('');
      setIsLoading(true);

      const registerData: RegisterRequest = {
        email: data.email,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        organization_id: data.organization_id && data.organization_id.trim() !== ''
          ? data.organization_id.trim()
          : undefined,
      };

      const tokens = await authApi.register(registerData);

      // Store tokens (backward compatibility; cookies may also be set by server).
      if (tokens?.access_token) {
        localStorage.setItem('access_token', tokens.access_token);
      }
      if (tokens?.refresh_token) {
        localStorage.setItem('refresh_token', tokens.refresh_token);
      }

      await new Promise((resolve) => setTimeout(resolve, 100));
      const userProfile = await usersApi.getProfile();
      login(userProfile, tokens);

      // Immediately open the in-app tour on Home after registration.
      navigate('/app/home?tour=1');
    } catch (err: any) {
      setError(err.detail || err.message || 'Registration failed');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } finally {
      setIsLoading(false);
    }
  };

  const shellSx = compact
    ? { pt: 0, pb: 1, maxWidth: 520, mx: 'auto' }
    : { p: 4, maxWidth: 500, mx: 'auto', mt: 8 };

  const inner = (
    <>
      {!compact && (
        <>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Sign Up
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
            Create your QuickField account. Leave organization empty to create a new one, or enter organization GUID to join existing.
          </Typography>
        </>
      )}
      {compact && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave organization empty to create a new one, or enter an organization GUID to join an existing organization.
        </Typography>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: compact ? 0 : 1 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              {...register('first_name')}
              margin="normal"
              required
              fullWidth
              id="first_name"
              label="First Name"
              name="first_name"
              autoComplete="given-name"
              autoFocus={!compact}
              error={!!errors.first_name}
              helperText={errors.first_name?.message}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              {...register('last_name')}
              margin="normal"
              required
              fullWidth
              id="last_name"
              label="Last Name"
              name="last_name"
              autoComplete="family-name"
              error={!!errors.last_name}
              helperText={errors.last_name?.message}
            />
          </Grid>
        </Grid>

        <TextField
          {...register('email')}
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          error={!!errors.email}
          helperText={errors.email?.message}
        />

        <TextField
          {...register('password')}
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          id="password"
          autoComplete="new-password"
          error={!!errors.password}
          helperText={errors.password?.message}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          {...register('organization_id')}
          margin="normal"
          fullWidth
          id="organization_id"
          label="Organization GUID (Optional)"
          name="organization_id"
          placeholder="Leave empty to create new organization"
          error={!!errors.organization_id}
          helperText={errors.organization_id?.message || 'Leave empty to create a new organization (named after your email) as a regular user, or enter organization GUID to join an existing one'}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Sign Up'}
        </Button>

        <Box textAlign="center">
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              onSwitchToLogin();
            }}
          >
            Already have an account? Sign In
          </Link>
        </Box>
      </Box>
    </>
  );

  if (compact) {
    return <Box sx={shellSx}>{inner}</Box>;
  }

  return (
    <Paper elevation={3} sx={shellSx}>
      {inner}
    </Paper>
  );
};
